import * as THREE from 'three';

const ZERO = new THREE.Matrix4().makeScale(0, 0, 0);

// Many copies of one shape in one draw call, with slots that can be taken and given back.
// A freed slot is folded to nothing until something else takes it.
export class Instances {
  // geometry: the shape, with its colours as vertex colours unless `colour` is true, in which case
  // each copy is given its own colour.
  constructor(scene, geometry, material, capacity, colour = false) {
    this.mesh = new THREE.InstancedMesh(geometry, material, capacity);
    // one copy folded to nothing is always drawn, so the pool's shader is compiled while the game
    // loads rather than the moment the first real copy appears
    this.mesh.count = 1;
    this.mesh.setMatrixAt(0, ZERO);
    this.mesh.castShadow = true;
    this.mesh.frustumCulled = false;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    if (colour) this.mesh.setColorAt(0, new THREE.Color(1, 1, 1));
    scene.add(this.mesh);
    this.capacity = capacity;
    this.free = [];
    this.used = 0; // slots handed out so far (the high-water mark)
  }

  // A slot for a new copy, or -1 if every slot is taken.
  add() {
    if (this.free.length) return this.free.pop();
    if (this.used >= this.capacity) return -1;
    const slot = this.used++;
    this.mesh.count = Math.max(1, this.used);
    return slot;
  }

  set(slot, matrix) {
    if (slot < 0) return;
    this.mesh.setMatrixAt(slot, matrix);
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  tint(slot, colour) {
    if (slot < 0 || !this.mesh.instanceColor) return;
    this.mesh.setColorAt(slot, colour);
    this.mesh.instanceColor.needsUpdate = true;
  }

  remove(slot) {
    if (slot < 0) return;
    this.mesh.setMatrixAt(slot, ZERO);
    this.mesh.instanceMatrix.needsUpdate = true;
    this.free.push(slot);
  }

  // For a pool whose slots are set from outside (wheels, four to a vehicle) rather than handed
  // out by add(): folds every slot to nothing, and draws as many as asked for.
  clear() {
    for (let i = 0; i < this.capacity; i++) this.mesh.setMatrixAt(i, ZERO);
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  draw(n) {
    const count = Math.max(1, Math.min(this.capacity, n));
    if (count > this.mesh.count) this.mesh.count = count;
  }
}
